// @flow
import * as React from 'react';
import get from 'lodash/get';
import head from 'lodash/head';
import { FormattedMessage } from 'react-intl';

import Button from '../../forms/Button';
import messages from '../../messages';
import styles from './styles';
import { Features } from '../../../components/mobile/Features';
import LocationContainer from '../../Location/LocationContainer';
import Icon from '../../../components/shared/Icons/Icon';
import { Price } from '../../../components/shared/Labels';
import { formatSecondsToRelativeTime } from '../../../utils/strings';
import getImageByMaker from '../../../utils/imagePlaceHolders/cars';
import FewResultsCars from '../../FewResultsMessages/cars';
import { getLocationItemByVerticalName } from '../../../utils/schemaOrg';
import { IMAGE_SIZE_320PX } from '../../../components/shared/Images/Image/constants';
import sortImagesByPreferredSize from '../../../components/shared/Images/Image/utils/sortImagesByPreferredSize';
import SnippetPicturePlaceholder from '../shared/SnippetPicturePlaceholder/component';
import SnippetPicture from '../shared/SnippetPicture/component';

type Props = {
  ad: Object,
  trovit: Object,
  searchParameters: Object,
  verticalName: VerticalName,
  countryCode: string,
  readingDirection: string,
  intl: Object,
  wasVisited: boolean,
  withRichData?: ?boolean,
  lazyLoadImage?: ?boolean,
};

const inlineStyles = {
  button: { margin: '5px 0 3px' },
  buttonIcon: { marginLeft: 2, verticalAlign: 'baseline' },
  photosIcon: { marginRight: 5, position: 'relative', top: -1 },
};

const { newAd, unavailablePrice, sponsoredTag, viewItem } = messages;

const ctaStyle = {
  margin: '0 0 0.5em 0',
  padding: '0.5em 0.3em 0.4em 0.8em',
  fontWeight: 'bold',
  fontSize: '14px',
  borderRadius: '5px',
};

function SnippetCars({
  ad,
  searchParameters,
  verticalName,
  countryCode,
  readingDirection,
  intl,
  trovit,
  wasVisited,
  withRichData,
  lazyLoadImage,
}: Props): React.Node {
  const isSemUrl = get(searchParameters, 'isSemUrl');
  const timeAgoSeconds = ad.timeAgoSeconds;
  const title = ad.title;
  const isPremium = ad.premium;
  const isFresh = ad.fresh;
  const location = ad.location;
  const verticalProperties = ad.verticalProperties;
  const price = verticalProperties.price;
  const make = verticalProperties.make;
  const isSimilarContentAd = ad.isSimilarContentAd;
  const locationRichDataItem = getLocationItemByVerticalName(verticalName);

  let images = sortImagesByPreferredSize(verticalProperties.images, IMAGE_SIZE_320PX);
  if (images && !images.length) {
    images = (make && getImageByMaker(make, trovit)) || [];
  }

  const totalImages = images && images.length && verticalProperties.totalImages;

  return (
    <span className={`snippet${wasVisited ? ' visited' : ''}`}>
      <h2 itemProp="description">{title}</h2>
      <div itemScope={withRichData} {...locationRichDataItem} className="snippet__location-wrapper">
        {(location.city ||
          location.region ||
          location.cityArea ||
          verticalProperties.address ||
          verticalProperties.geoOsm) && (
          <LocationContainer
            region={location.region}
            city={location.city}
            cityArea={location.cityArea}
            address={verticalProperties.address}
            geoInfo={{
              geoOsm: verticalProperties.geoOsm,
              numVisibleItems: -1,
              postalCode: location.postalCode,
            }}
            withRichData={withRichData}
          />
        )}
      </div>

      {price.value ? (
        <Price
          value={price.value}
          valueFormatted={price.valueFormatted}
          oldValue={price.oldValue}
          oldValueFormatted={price.oldValueFormatted}
          withRichData={withRichData}
        />
      ) : (
        <p className="noPrice">
          <FormattedMessage {...unavailablePrice} />
        </p>
      )}

      {isSimilarContentAd ? (
        <FewResultsCars
          make={get(searchParameters, 'carsMake')}
          model={get(searchParameters, 'carsModel')}
        />
      ) : (
        <p className="date">
          {formatSecondsToRelativeTime(timeAgoSeconds, intl)}
          <span className="source-label"> {get(ad, ['source', 'name'])}</span>
        </p>
      )}

      <Button
        type="button"
        title={<FormattedMessage {...viewItem} />}
        color="orange"
        style={ctaStyle}
        icon={<Icon type="arrow-right" width={18} />}
        iconPosition="after"
      />

      <Features
        verticalName={verticalName}
        verticalProps={verticalProperties}
        country={{
          code: countryCode,
          readingDirection,
        }}
        description={ad.description}
      />

      <div className="img">
        {images && !!images.length ? (
          <SnippetPicture
            image={head(images)}
            lazyLoad={lazyLoadImage}
            placeholder={
              <SnippetPicturePlaceholder
                verticalName={verticalName}
                style={inlineStyles.photosIcon}
              />
            }
          />
        ) : (
          <SnippetPicturePlaceholder verticalName={verticalName} style={inlineStyles.photosIcon} />
        )}

        {totalImages && totalImages > 1 ? (
          <span className="photos" data-test="photos">
            <Icon style={inlineStyles.photosIcon} type="photos" />
            {totalImages}
          </span>
        ) : null}

        {isPremium && !isSemUrl && (
          <p className="sponsored">
            <FormattedMessage {...sponsoredTag} />
          </p>
        )}

        {isFresh && !isPremium && (
          <span className="new">
            <FormattedMessage {...newAd} />
          </span>
        )}
      </div>

      <style jsx>{styles}</style>
    </span>
  );
}

SnippetCars.defaultProps = {
  withRichData: null,
  lazyLoadImage: false,
};

export default SnippetCars;



// WEBPACK FOOTER //
// ./app/common/Snippet/cars/component.js